"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Drawer } from "vaul";
import { useAppLauncherContext } from "../providers/app-launcher-provider";
import { useGlobalMenu } from "../queries/use-global-menu";
import type { GlobalMenuItem } from "../types";
import {
  getGlobalMenuItemStatus,
  getSortedGlobalMenuItems,
  resolveGlobalMenuItemAssetUrl,
} from "../menu";
import { BackArrowIcon, CloseIcon } from "./icons";
import { cn } from "../../../lib/utils";
import { TrackingBoundary } from "../../../lib/sentry/index";

export interface AppLauncherContentProps {
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
}

function isStakingItem(item: GlobalMenuItem) {
  return item.id === "cross-staking" || item.type === "cross-staking";
}

export function AppLauncherContent({
  align = "end",
  sideOffset = 8,
  className,
}: AppLauncherContentProps) {
  const { env, theme, isMobile, domain, close } =
    useAppLauncherContext();
  const { data } = useGlobalMenu(env);
  const menuItems = React.useMemo(
    () => getSortedGlobalMenuItems(data?.items ?? []),
    [data?.items],
  );

  /**
   * 드로어를 스와이프하다 손가락이 항목 위에서 떨어졌을 때를 걸러내는 임계값.
   *
   * 브라우저는 자체 tap slop(모바일 보통 10~25px)을 넘지 않았을 때만 `click` 을
   * 발생시킨다 — 즉 여기 도달한 시점에 이미 "탭"으로 판정된 것이다. 예전 값(8px)은
   * 그보다 엄격해서, 손가락이 조금만 흔들려도 브라우저가 보낸 클릭을 우리가 다시
   * 버렸다(모바일에서 가끔 눌리지 않던 원인).
   */
  const DRAG_THRESHOLD = 24;
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);
  const wasDrag = React.useRef(false);

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
    wasDrag.current = false;
  }, []);

  // 판정은 pointerup 에서 끝낸다 — click 은 pointerup 뒤에 오므로 여기서 좌표를
  // 지우면 click 시점엔 비교할 게 남지 않는다.
  const handlePointerUp = React.useCallback((e: React.PointerEvent) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    wasDrag.current =
      Math.abs(e.clientX - start.x) > DRAG_THRESHOLD ||
      Math.abs(e.clientY - start.y) > DRAG_THRESHOLD;
  }, []);

  // 스크롤·스와이프로 취소된 포인터는 클릭이 아니다.
  const handlePointerCancel = React.useCallback(() => {
    pointerStart.current = null;
    wasDrag.current = true;
  }, []);

  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname : "";

  const handleItemClick = React.useCallback(
    (e: React.MouseEvent, item: GlobalMenuItem) => {
      // `detail === 0` 은 키보드·스크린리더가 만든 클릭이다(좌표가 없으므로
      // 드래그 판정을 적용하면 안 된다).
      const dragged = wasDrag.current;
      wasDrag.current = false;
      if (dragged && e.detail !== 0) return;

      const url = item.url[env] ?? item.url.production;
      const isExternal = isExternalLink(url, domain);

      if (!isExternal) {
        try {
          // 쿼리·해시·하위 경로가 하나라도 다르면 이동해야 한다.
          // 완전히 같은 페이지일 때만 리로드 대신 메뉴만 닫는다.
          const target = new URL(url);
          const isSamePage =
            target.pathname === window.location.pathname &&
            target.search === window.location.search &&
            target.hash === window.location.hash;
          if (isSamePage) {
            close();
            return;
          }
        } catch {}
      }

      if (isExternal) {
        // 인앱 브라우저/웹뷰는 window.open 을 조용히 차단하는 경우가 있다
        // (반환값 null). 그때는 아래 같은 창 이동으로 폴백한다.
        // 주의: feature 에 noopener 를 넣으면 성공해도 반환값이 null 이라
        // (스펙 동작) 차단 여부를 알 수 없다 — 열린 창의 opener 를 직접 끊는다.
        const opened = window.open(url, "_blank");
        if (opened) {
          opened.opener = null;
          close();
          return;
        }
      }

      // 같은 창 이동에서는 close() 를 부르면 안 된다 — useHistoryDismiss 가
      // 닫힘을 감지하고 history.go(-1) 로 되감으면서 방금 시작된 네비게이션을
      // 취소해 버린다(1.3.15 회귀: 메뉴만 닫히고 이동이 안 되던 원인).
      // 페이지가 언로드되면서 메뉴도 함께 사라진다.
      window.location.href = url;
    },
    [env, domain, close],
  );

  const renderMenuItem = (item: GlobalMenuItem) => {
    const url = item.url[env] ?? item.url.production;
    const isExternal = isExternalLink(url, domain);
    const serviceStatus = getGlobalMenuItemStatus(item);
    const isEndingSoon = serviceStatus === "ending";
    const iconUrl = resolveGlobalMenuItemAssetUrl(item.iconUrl, env);
    let isActive = false;
    if (!isExternal) {
      try {
        const urlPathname = new URL(url).pathname;
        isActive =
          currentPathname === urlPathname ||
          currentPathname.startsWith(urlPathname + "/");
      } catch {
        isActive = false;
      }
    }

    return (
      <button
        key={item.id}
        type="button"
        className="al-grid-item"
        data-track="menu_item_click"
        data-track-id={item.id}
        data-active={isActive || undefined}
        data-service-status={serviceStatus}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={(e) => handleItemClick(e, item)}
      >
        <div className="al-grid-item-left">
          <div className="al-grid-item-icon">
            <img
              src={iconUrl}
              alt={item.label}
              width={24}
              height={24}
              loading="lazy"
            />
            {item.isNew && (
              <span className="al-status-ribbon al-status-ribbon-new">N</span>
            )}
          </div>
          <span className="al-grid-item-text">
            <span className="al-grid-item-label">{item.label}</span>
            {isEndingSoon && (
              <span className="al-grid-item-status-text">(End Soon)</span>
            )}
            {item.badge && !isStakingItem(item) && (
              <span className="al-badge al-badge-list">{item.badge}</span>
            )}
          </span>
        </div>
      </button>
    );
  };

  const menuContent = (
    <div className="al-menu" data-theme={theme}>
      <div className="al-grid">{menuItems.map(renderMenuItem)}</div>
    </div>
  );

  if (isMobile) {
    return (
      <TrackingBoundary feature="app_launcher">
        <Drawer.Portal>
          <Drawer.Overlay className="al-drawer-overlay" />
          <Drawer.Content
            className={cn("al-drawer-content", className)}
            data-theme={theme}
          >
            <div className="al-drawer-header">
              <Drawer.Close className="al-drawer-close" data-track="close">
                <BackArrowIcon />
              </Drawer.Close>
              <Drawer.Title className="al-drawer-title">
                ONE Services
              </Drawer.Title>
              <Drawer.Close className="al-drawer-close" data-track="close">
                <CloseIcon />
              </Drawer.Close>
            </div>
            <div className="al-drawer-body">{menuContent}</div>
          </Drawer.Content>
        </Drawer.Portal>
      </TrackingBoundary>
    );
  }

  return (
    <TrackingBoundary feature="app_launcher">
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={sideOffset}
          className={cn("al-popover-content", className)}
          data-theme={theme}
        >
          {menuContent}
        </Popover.Content>
      </Popover.Portal>
    </TrackingBoundary>
  );
}

function isExternalLink(url: string, domain?: string): boolean {
  try {
    const linkHostname = new URL(url).hostname;
    if (typeof window === "undefined") return true;
    return linkHostname !== (domain ?? window.location.hostname);
  } catch {
    return false;
  }
}
